import { useState } from "react";
import { lineGroups, statusLegend } from "@/mocks/movieData";
import SectionHeading, { Section } from "./SectionHeading";

const statusStyles: Record<string, string> = {
  ok: "bg-secondary-100 text-secondary-900 border-secondary-300",
  busy: "bg-accent-100 text-accent-900 border-accent-300",
  maintain: "bg-background-200 text-foreground-700 border-background-300",
};

const dotStyles: Record<string, string> = {
  ok: "bg-secondary-500",
  busy: "bg-accent-500",
  maintain: "bg-foreground-400",
};

export default function LineStatus() {
  const [activeTab, setActiveTab] = useState(lineGroups[0].id);
  const activeGroup = lineGroups.find((g) => g.id === activeTab) ?? lineGroups[0];

  return (
    <Section id="lines" className="bg-background-100">
      <SectionHeading
        eyebrow="地址与线路"
        accent="accent"
        title="最新地址与线路状态汇总"
        description="同一时间通常会有多条可用的线路，它们的区别主要在加载速度和稳定性。下面按使用终端分组，先看状态再选择，比盲目换地址更省时间。"
      />

      <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="线路分组">
        {lineGroups.map((group) => {
          const isActive = group.id === activeTab;
          return (
            <button
              key={group.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(group.id)}
              className={`px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer border ${
                isActive
                  ? "bg-primary-500 text-background-50 border-primary-500"
                  : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {group.tabLabel}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-foreground-600 mb-5">{activeGroup.intro}</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        {activeGroup.lines.map((line) => (
          <article
            key={line.id}
            className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">{line.name}</h3>
                <p className="mt-1 text-xs text-foreground-500 font-label">分组：{line.region}</p>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-label border whitespace-nowrap ${statusStyles[line.statusKey]}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${dotStyles[line.statusKey]}`}></span>
                {line.status}
              </span>
            </div>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">加载速度</dt>
                <dd className="text-foreground-900 font-label">{line.speed}</dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-foreground-500 whitespace-nowrap">使用建议</dt>
                <dd className="text-foreground-700 text-right">{line.note}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>

      <div className="mt-8 rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">状态含义说明</h3>
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {statusLegend.map((item) => (
            <li key={item.key} className="flex items-start gap-3">
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dotStyles[item.key]}`}></span>
              <div>
                <p className="text-sm font-label text-foreground-900">{item.label}</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-0.5">{item.desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}